import React, { useCallback, useEffect, useState } from 'react';
import { ArrowPathIcon } from '@heroicons/react/24/outline';
import { TASK_TYPE_LABELS } from '@claimup/shared/constants/assignments';
import api, { getErrorMessage } from '../../../services/api';

/**
 * De todo lo que el agente propone, cuánto acaba saliendo.
 *
 * 🔴 Es el RESULTADO, no el trabajo en curso. La pestaña de Revisión enseña lo
 * que espera a una persona; esto enseña en qué acabó, día a día y por tipo de
 * tarea. Sin ella, un tipo que se rechaza sistemáticamente se ve igual que uno
 * sano: los dos «generan propuestas».
 *
 * 🔴 «SALIÓ» ES LA TAREA COMPLETADA, no el plan aprobado. Aprobar un WhatsApp
 * del carril del navegador **no envía nada**: deja la orden y el brazo la recoge
 * cuando pasa. El 16/09/2026 entre las dos cosas hubo una mediana de 15,8
 * minutos y un caso de casi seis horas. Por eso hay una columna para lo que se
 * aprobó y no llegó a hacerse: es lo que parece trabajo terminado y no lo es.
 */

const RANGOS = [
  { dias: 7, nombre: '7 días' },
  { dias: 30, nombre: '30 días' },
  { dias: 90, nombre: '90 días' },
];

const fechaCorta = (dia) => {
  const [a, m, d] = dia.split('-');
  return new Date(Date.UTC(+a, +m - 1, +d)).toLocaleDateString('es-ES', {
    day: '2-digit',
    month: 'short',
    timeZone: 'UTC',
  });
};

// Las clases van literales: Tailwind purga lo que no encuentre escrito tal cual.
const Celda = ({ children, apagada = false, fuerte = false, alerta = false }) => (
  <td
    className={`px-3 py-2 text-right tabular-nums ${
      alerta
        ? 'font-medium text-amber-700'
        : fuerte
          ? 'font-medium text-slate-900'
          : apagada
            ? 'text-slate-300'
            : 'text-slate-600'
    }`}
  >
    {children}
  </td>
);

const AIAgentRendimiento = () => {
  const [datos, setDatos] = useState(null);
  const [error, setError] = useState(null);
  const [cargando, setCargando] = useState(true);
  const [dias, setDias] = useState(30);
  const [tipo, setTipo] = useState('todos');

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const { data } = await api.get(`/ai-agent/rendimiento?dias=${dias}`);
      setDatos(data.data);
      setError(null);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setCargando(false);
    }
  }, [dias]);

  useEffect(() => {
    cargar();
  }, [cargar]);

  if (cargando && !datos) {
    return <p className='text-sm text-slate-500'>Cargando el rendimiento…</p>;
  }

  if (error) {
    return (
      <div className='bg-white rounded-lg border border-red-200 px-6 py-6'>
        <p className='text-sm text-red-700'>{error}</p>
      </div>
    );
  }

  const todas = datos?.filas || [];
  const filas = tipo === 'todos' ? todas : todas.filter((f) => f.task_type === tipo);
  const tiposConDatos = [...new Set(todas.map((f) => f.task_type))].sort();

  const suma = (campo) => filas.reduce((t, f) => t + (f[campo] || 0), 0);
  const propuestas = suma('propuestas');
  const salio = suma('salio');

  return (
    <div className='bg-white rounded-lg border border-slate-200'>
      <div className='px-6 py-4 border-b border-slate-200 flex items-start justify-between gap-4'>
        <div>
          <h2 className='text-base font-semibold text-slate-900'>
            Rendimiento del agente, día a día
          </h2>
          <p className='text-sm text-slate-500 mt-0.5'>
            De lo que propone, cuánto acaba saliendo. Todos los tipos de tarea, no solo WhatsApp.
          </p>
        </div>
        <div className='flex items-center gap-2'>
          <select
            value={tipo}
            onChange={(e) => setTipo(e.target.value)}
            className='rounded-lg border border-slate-300 text-sm px-2 py-1.5 text-slate-700'
          >
            <option value='todos'>Todos los tipos</option>
            {tiposConDatos.map((t) => (
              <option key={t} value={t}>
                {TASK_TYPE_LABELS[t] || t}
              </option>
            ))}
          </select>
          <div className='inline-flex rounded-lg border border-slate-300 overflow-hidden'>
            {RANGOS.map((r) => (
              <button
                key={r.dias}
                type='button'
                onClick={() => setDias(r.dias)}
                className={`px-3 py-1.5 text-sm ${
                  dias === r.dias
                    ? 'bg-indigo-600 text-white'
                    : 'bg-white text-slate-600 hover:bg-slate-50'
                }`}
              >
                {r.nombre}
              </button>
            ))}
          </div>
          <button
            type='button'
            onClick={cargar}
            className='rounded-lg border border-slate-300 p-1.5 text-slate-500 hover:bg-slate-50'
            title='Actualizar'
          >
            <ArrowPathIcon className='w-4 h-4' />
          </button>
        </div>
      </div>

      {/* Una sola cifra arriba, y es la que resume la pantalla: de cada cien
          propuestas, cuántas acaban haciéndose. */}
      <div className='px-6 py-4 border-b border-slate-200'>
        <p className='text-2xl font-semibold tabular-nums text-slate-900'>
          {propuestas ? Math.round((salio * 100) / propuestas) : 0}%
        </p>
        <p className='text-sm text-slate-700 mt-0.5'>
          de las propuestas acaban saliendo
        </p>
        <p className='text-xs text-slate-500 mt-0.5'>
          {salio} de {propuestas} en {datos?.dias} días
        </p>
      </div>

      <div className='overflow-x-auto'>
        <table className='w-full text-sm'>
          <thead>
            <tr className='text-xs uppercase tracking-wide text-slate-400 text-left border-b border-slate-200'>
              <th className='px-3 py-2 font-medium'>Día</th>
              <th className='px-3 py-2 font-medium'>Tarea</th>
              <th className='px-3 py-2 font-medium text-right'>Propuestas</th>
              <th className='px-3 py-2 font-medium text-right border-l border-slate-100'>Aprobadas</th>
              <th className='px-3 py-2 font-medium text-right'>Rechazadas</th>
              <th className='px-3 py-2 font-medium text-right'>Esperando</th>
              <th className='px-3 py-2 font-medium text-right'>Sin veredicto</th>
              <th className='px-3 py-2 font-medium text-right border-l border-slate-100'>Salió</th>
              <th className='px-3 py-2 font-medium text-right'>Aprob. sin salir</th>
            </tr>
          </thead>
          <tbody className='divide-y divide-slate-100'>
            {filas.map((f) => (
              <tr key={`${f.dia}|${f.task_type}`}>
                <td className='px-3 py-2 text-slate-500 whitespace-nowrap'>{fechaCorta(f.dia)}</td>
                <td className='px-3 py-2 text-slate-800 whitespace-nowrap'>
                  {TASK_TYPE_LABELS[f.task_type] || f.task_type}
                </td>
                <Celda fuerte>{f.propuestas}</Celda>
                <Celda apagada={!f.aprobadas}>{f.aprobadas}</Celda>
                <Celda apagada={!f.rechazadas}>{f.rechazadas}</Celda>
                <Celda apagada={!f.pendientes}>{f.pendientes}</Celda>
                <Celda apagada={!f.sin_veredicto}>{f.sin_veredicto}</Celda>
                <Celda fuerte={f.salio > 0} apagada={!f.salio}>
                  {f.salio}
                </Celda>
                {/* En ámbar porque es la única columna que señala algo roto:
                    alguien dijo que sí y el trabajo no llegó a hacerse. */}
                <Celda apagada={!f.aprobadas_sin_salir} alerta={f.aprobadas_sin_salir > 0}>
                  {f.aprobadas_sin_salir}
                </Celda>
              </tr>
            ))}
          </tbody>
        </table>
        {!filas.length && (
          <p className='px-6 py-8 text-sm text-slate-500'>
            El agente no propuso nada en este rango.
          </p>
        )}
      </div>

      <div className='px-6 py-3 border-t border-slate-200 text-xs text-slate-500 space-y-1'>
        <p>
          <span className='font-medium text-slate-600'>Salió</span> es la tarea completada, no el
          plan aprobado: aprobar un WhatsApp del navegador deja la orden y el brazo la recoge
          cuando pasa. Por eso «Aprob. sin salir» no es cero.
        </p>
        <p>
          <span className='font-medium text-slate-600'>Sin veredicto</span> son las que salieron de
          la cola sin que nadie las juzgara: se propuso algo mejor para la misma tarea
          («superseded») o no necesitaban revisión («auto»).
        </p>
      </div>
    </div>
  );
};

export default AIAgentRendimiento;
